import React, { useState, useRef, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import rrulePlugin from '@fullcalendar/rrule';
import googleCalendarPlugin from '@fullcalendar/google-calendar';
import listPlugin from '@fullcalendar/list';
import EventModal from './modals/EventModal';
import AddEventModal from './modals/AddEventModal';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import { useOrganizationContext } from '../../../context/organization_context/OrganizationContext';
import { Box, useTheme, useMediaQuery, IconButton, Fab, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '../../../context/user_context/UserContext';
import { useGoogleCalendarContext } from '../../../context/google_calendar/GoogleCalendarContext';
import { useFirebaseEvents } from './hooks/useFirebaseEvents';
import allLocales from '@fullcalendar/core/locales-all';
import AddIcon from '@mui/icons-material/Add';
import ViewDayIcon from '@mui/icons-material/ViewDay';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import GoogleIcon from '@mui/icons-material/Google';
import SyncIcon from '@mui/icons-material/Sync';
import Logo from '../../../resources/images/logo/bizismart.png';
import { StringKeys } from '../../../resources/strings/StringKeys';

function Calendar() {
  const { firebaseUser } = useUserContext();
  const { organizationCustomers, organizationEmployees } = useOrganizationContext();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const calendarRef = useRef(null);
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentView, setCurrentView] = useState(isMobile ? 'listWeek' : 'dayGridMonth');

  const {
    googleAccessToken,
    externalEvents,
    addToGoogleCalendar,
    isGoogleConnected,
    syncCalendar,
    isLoading,
    updateGoogleEvent
  } = useGoogleCalendarContext();

  const { events, addEvent, deleteEvent, updateEvent } = useFirebaseEvents(
    firebaseUser,
    organizationEmployees,
    organizationCustomers
  );

  // Memoize the combined events to prevent unnecessary recalculations
  const allEvents = useMemo(() => {

    const firebaseEventsList = events.map(event => {
      const matchingGoogleEvent = externalEvents.find(gEvent => event.googleEventId === gEvent.id);

      return {
        id: `fb-${event.id}`,
        title: event.title,
        start: event.start?.toDate?.() || new Date(event.start),
        end: event.end?.toDate?.() || new Date(event.end),
        allDay: event.all_day,
        description: event.description,
        location: event.location,
        backgroundColor: matchingGoogleEvent ? '#1976d2' : '#023047', // Light blue if both, dark blue if Firebase only
        borderColor: 'transparent',
        textColor: '#ffffff',
        editable: true,
        extendedProps: {
          ...event,
          source: 'firebase',
          originalId: event.id,
          isGoogleLinked: !!matchingGoogleEvent
        }
      };
    });

    const googleEventsList = externalEvents
      .filter(googleEvent =>
        !events.some(fbEvent => fbEvent.googleEventId === googleEvent.id)
      )
      .map(event => ({
        id: `gc-${event.id}`,
        title: event.title || '',
        start: event.start,
        end: event.end,
        allDay: !event.start?.includes('T'),
        url: event.url,
        backgroundColor: '#1976d2',
        borderColor: 'transparent',
        textColor: '#ffffff',
        editable: false,
        extendedProps: {
          ...event.extendedProps,
          source: 'google',
          originalId: event.id,
          description: event.description || '',
          location: event.location || ''
        }
      }));

    const combined = [...firebaseEventsList, ...googleEventsList];
    return combined;
  }, [events, externalEvents]);

  const handleDateClick = (arg) => {
    setSelectedDate(arg.date);
    setShowAddEventModal(true);
  };

  const handleEventClick = (clickInfo) => {
    if (clickInfo.event.url) {
      clickInfo.jsEvent.preventDefault();
    }

    const eventSource = clickInfo.event.extendedProps?.source;

    // Create a clean copy of the event data
    const eventCopy = {
      id: clickInfo.event.id,
      title: clickInfo.event.title,
      start: clickInfo.event.start,
      end: clickInfo.event.end || clickInfo.event.start, // Fallback to start if end is null
      url: clickInfo.event.url || '', // Ensure url is never null
      backgroundColor: clickInfo.event.backgroundColor,
      borderColor: clickInfo.event.borderColor,
      textColor: clickInfo.event.textColor,
      extendedProps: {
        ...clickInfo.event.extendedProps,
        source: eventSource  // Preserve the source
      }
    };

    setSelectedEvent(eventCopy);
    setShowEventModal(true);
  };

  const handleModalClose = () => {
    setSelectedEvent(null);
    setShowEventModal(false);
  };

  const handleEventDelete = async (firebaseEventId, googleEventId) => {
    const result = await deleteEvent(firebaseEventId, googleEventId);
    if (result.success) {
      handleModalClose();
      // If it was a Google event or linked to Google, sync the calendar
      if (googleEventId) {
        // Wait a bit to ensure deletion is processed
        setTimeout(() => {
          syncCalendar();
        }, 1000);
      }
    }
    return result;
  };

  const handleViewChange = (newView) => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.changeView(newView);
      setCurrentView(newView);
    }
  };

  const handleEventEdit = async (eventData, originalEvent) => {
    try {
      if (!originalEvent) {
        console.error('Original event data is missing');
        return { success: false, error: 'Original event data is missing' };
      }

      let result;
      const isGoogleEvent = originalEvent.extendedProps?.source === 'google';
      const isLinkedToGoogle = originalEvent.extendedProps?.isGoogleLinked;

      // Convert dates to the appropriate format for each service
      const googleEventData = {
        ...eventData,
        start: {
          toDate: () => new Date(eventData.start)
        },
        end: {
          toDate: () => new Date(eventData.end)
        }
      };

      const firebaseEventData = {
        ...eventData,
        start: new Date(eventData.start),
        end: new Date(eventData.end)
      };

      if (isGoogleEvent || isLinkedToGoogle) {
        // Update in Google Calendar
        result = await updateGoogleEvent(googleEventData, originalEvent);
        if (!result.success) {
          return result;
        }
      }

      if (!isGoogleEvent) {
        // Update in Firebase
        result = await updateEvent(firebaseUser.organization, originalEvent.extendedProps.originalId, firebaseEventData);
      }

      if (result.success) {
        // Wait a bit before syncing to ensure updates are processed
        setTimeout(() => {
          syncCalendar();
        }, 1000);
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating event:', error);
      return { success: false, error };
    }
  };

  const renderViewButtons = () => (
    <Box sx={{
      display: 'flex',
      gap: isMobile ? 0.5 : 1,
      mb: 2,
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: isMobile ? 'wrap' : 'nowrap',
      width: '100%'
    }}>
      <Box sx={{ 
        display: 'flex', 
        gap: isMobile ? 0.5 : 1,
        flexGrow: 0 
      }}>
        <IconButton
          onClick={() => handleViewChange('timeGridDay')}
          color={currentView === 'timeGridDay' ? 'primary' : 'default'}
          size="small"
          sx={isMobile ? {
            minWidth: 0,
            padding: '4px',
            '& .MuiTypography-root': { display: 'none' }
          } : {}}
        >
          <ViewDayIcon />
          {!isMobile && <Typography sx={{ ml: 0.5 }}>Day</Typography>}
        </IconButton>
        <IconButton
          onClick={() => handleViewChange('timeGridWeek')}
          color={currentView === 'timeGridWeek' ? 'primary' : 'default'}
          size="small"
          sx={isMobile ? {
            minWidth: 0,
            padding: '4px',
            '& .MuiTypography-root': { display: 'none' }
          } : {}}
        >
          <ViewWeekIcon />
          {!isMobile && <Typography sx={{ ml: 0.5 }}>Week</Typography>}
        </IconButton>
        <IconButton
          onClick={() => handleViewChange('dayGridMonth')}
          color={currentView === 'dayGridMonth' ? 'primary' : 'default'}
          size="small"
          sx={isMobile ? {
            minWidth: 0,
            padding: '4px',
            '& .MuiTypography-root': { display: 'none' }
          } : {}}
        >
          <CalendarViewMonthIcon />
          {!isMobile && <Typography sx={{ ml: 0.5 }}>Month</Typography>}
        </IconButton>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        gap: isMobile ? 0.5 : 1,
        flexGrow: 0 
      }}>
        {isGoogleConnected && (
          <IconButton
            onClick={() => syncCalendar()}
            color="primary"
            size="small"
            disabled={isLoading}
            sx={isMobile ? {
              minWidth: 0,
              padding: '4px',
              '& .MuiTypography-root': { display: 'none' }
            } : {}}
          >
            <SyncIcon sx={{
              animation: isLoading ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }} />
            {!isMobile && <Typography sx={{ ml: 1 }}>{isLoading ? t(StringKeys.SYNCING) : t(StringKeys.SYNC)}</Typography>}
          </IconButton>
        )}
        <IconButton
          color="primary"
          aria-label="add"
          onClick={() => setShowAddEventModal(true)}
          sx={isMobile ? {
            minWidth: 0,
            padding: '4px',
            '& .MuiTypography-root': { display: 'none' }
          } : {}}
        >
          <AddIcon />
          {!isMobile && <Typography sx={{ ml: 0.5 }}>{t(StringKeys.ADD_EVENT)}</Typography>}
        </IconButton>
      </Box>
    </Box>
  );

  const handleAddEvent = async (organization, eventData) => {
    try {
      const result = await addEvent(organization, eventData);
      if (result.success) {
        // Wait a bit before syncing to ensure Firebase update is complete
        setTimeout(async () => {
          await syncCalendar();
        }, 1000);
      }
      return result;
    } catch (error) {
      console.error('Error adding event:', error);
      throw error;
    }
  };

  return (
    <>
      <AppSidebar
        isCalendar={true}
        content={
          <Box sx={{
            p: isMobile ? 1 : 2,
            backgroundColor: 'background.paper',
            borderRadius: 1,
            boxShadow: 1,
            height: 'calc(100vh - 100px)',
            display: 'flex',
            flexDirection: 'column',
            '& .fc': {
              // Calendar container
              '--fc-page-bg-color': 'transparent',
              '--fc-border-color': theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',

              // Headers
              '--fc-col-header-bg-color': theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
              '--fc-col-header-text-color': theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'inherit',

              // Today column
              '--fc-today-bg-color': theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',

              // General text
              '--fc-list-event-hover-bg-color': theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
              color: theme.palette.text.primary,

              // Mobile-specific styles
              ...(isMobile && {
                // Optimize header toolbar for mobile
                '& .fc-header-toolbar': {
                  marginBottom: '0.5rem',
                  padding: '0.5rem 0',
                  flexDirection: 'row',
                  gap: '4px',
                  '& .fc-toolbar-title': {
                    fontSize: '1rem',
                    fontWeight: 500,
                  },
                  '& .fc-button': {
                    padding: '4px 8px',
                    fontSize: '0.8rem',
                  },
                },

                // Optimize day cells for mobile
                '& .fc-daygrid-day': {
                  '& .fc-daygrid-day-frame': {
                    minHeight: '70px',
                    padding: '2px',
                    display: 'flex',
                    flexDirection: 'column',
                  },
                  '& .fc-daygrid-day-number': {
                    fontSize: '0.9rem',
                    padding: '2px 4px',
                  },
                },

                // Optimize events display for better touch interaction
                '& .fc-daygrid-event': {
                  margin: '2px 0',
                  padding: '3px 0',
                  height: '18px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  '&:active': {
                    opacity: 0.7
                  },
                },

                // Optimize week day headers
                '& .fc-col-header-cell': {
                  padding: '4px 0',
                  '& .fc-col-header-cell-cushion': {
                    padding: '4px',
                    fontSize: '0.7rem',
                  },
                },

                // Optimize more events link
                '& .fc-daygrid-more-link': {
                  fontSize: '0.7rem',
                  margin: '0 2px',
                  padding: '0 2px',
                },

                // Ensure proper spacing between events
                '& .fc-daygrid-event-harness': {
                  margin: '1px 0',
                },
              }),
            },
            '& .fc th': {
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'
            },
            '& .fc-col-header-cell-cushion': {
              color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
              padding: '8px'
            }
          }}
          >
            {renderViewButtons()}
            <div style={{
              flex: 1,
              minHeight: 0,
              position: 'relative'
            }}>
              <FullCalendar
                ref={calendarRef}
                locale={currentLang}
                locales={allLocales}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, rrulePlugin, googleCalendarPlugin, listPlugin]}
                height="auto"
                events={allEvents}
                initialView={currentView}
                dateClick={handleDateClick}
                eventClick={handleEventClick}
                selectable={true}
                headerToolbar={{
                  left: isMobile ? 'prev,next' : 'prev,next today',
                  center: 'title',
                  right: isMobile ? 'today' : ''
                }}
                eventDidMount={(info) => {
                  // Add source-specific styling or behavior
                  if (info.event.extendedProps?.source === 'google') {
                    info.el.style.cursor = 'pointer';
                  }
                }}
                views={{
                  dayGridMonth: {
                    titleFormat: { year: 'numeric', month: 'long' },
                    ...(isMobile && {
                      dayMaxEventRows: 4,
                      moreLinkContent: (args) => {
                        return `+${args.num} more`;
                      },
                    })
                  },
                  timeGridWeek: {
                    titleFormat: { year: 'numeric', month: 'short', day: '2-digit' }
                  },
                  timeGridDay: {
                    titleFormat: { year: 'numeric', month: 'long', day: '2-digit', weekday: 'long' }
                  },
                  listWeek: {
                    titleFormat: { year: 'numeric', month: 'long' }
                  }
                }}
                eventDisplay="block"
                eventTimeFormat={{
                  hour: 'numeric',
                  minute: '2-digit',
                  meridiem: 'short'
                }}
                dayMaxEvents={isMobile ? false : true}
                stickyHeaderDates={true}
                eventContent={(eventInfo) => {
                  // Only show simplified events in mobile month view
                  if (isMobile && currentView === 'dayGridMonth') {
                    return <div style={{
                      width: '100%',
                      height: '100%',
                      cursor: 'pointer',
                      backgroundColor: eventInfo.event.backgroundColor
                    }} />;
                  }

                  // For desktop or non-month views, use the original event display
                  const isFirebase = eventInfo.event.extendedProps?.source === 'firebase';
                  const isGoogle = eventInfo.event.extendedProps?.source === 'google';
                  const isLinked = eventInfo.event.extendedProps?.isGoogleLinked;
                  const isDarkMode = theme.palette.mode === 'dark';

                  // Determine if we need to adjust icon colors
                  const isListView = currentView === 'listWeek';
                  const isTimeGridView = currentView.includes('timeGrid');
                  const needsInvertedColors = (isDarkMode && currentView === 'dayGridMonth');

                  // Calculate icon color - always white for list view and time grid views
                  const iconColor = (isListView || isTimeGridView) ? '#ffffff' :
                    (needsInvertedColors ?
                      (isDarkMode ? '#ffffff' : '#023047') :
                      '#ffffff');

                  return (
                    <div style={{
                      padding: '2px 4px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      backgroundColor: eventInfo.event.backgroundColor,
                      color: '#ffffff', // Always white text
                      borderRadius: '4px'
                    }}>
                      {isFirebase && (
                        <img
                          src={Logo}
                          alt=""
                          style={{
                            width: 16,
                            height: 16,
                            marginRight: '2px',
                            filter: (isListView || isTimeGridView) ?
                              'brightness(0) invert(1)' :  // Always white in list view and time grid
                              (needsInvertedColors ?
                                (isDarkMode ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%)') :
                                'none')
                          }}
                        />
                      )}
                      {(isGoogle || isLinked) && (
                        <GoogleIcon
                          sx={{
                            width: 16,
                            height: 16,
                            color: iconColor
                          }}
                        />
                      )}
                      <span>{eventInfo.event.title}</span>
                    </div>
                  );
                }}
              />
            </div>
          </Box>
        }
      />
      <EventModal
        show={showEventModal}
        onHide={handleModalClose}
        event={selectedEvent}
        onDelete={handleEventDelete}
        onEdit={handleEventEdit}
      />
      <AddEventModal
        show={showAddEventModal}
        onHide={() => setShowAddEventModal(false)}
        selectedDate={selectedDate}
        googleAccessToken={googleAccessToken}
        isGoogleConnected={isGoogleConnected}
        onAdd={handleAddEvent}
        onAddToGoogle={addToGoogleCalendar}
      />
    </>
  );
}

export default Calendar;
